/**
 * Iter218 · /private-vehicle-security — Smith SecureVehicle™ landing page.
 *
 * Public page. Positions Smith as *Private Vehicle Intelligence & Property
 * Security* — customer-controlled, never a public surveillance network.
 * CTA drives to the 16-question assessment form at `/security-assessment`.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Car, ShieldCheck, Eye, Building2, Users, ArrowRight, CheckCircle2,
  AlertTriangle, MapPin, Radio, Scale, Sparkles, ChevronRight,
} from "lucide-react";


function useSeo() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Smith SecureVehicle™ — Private Vehicle Intelligence & Property Security";
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description",
      "Smith Protective Services — private, customer-controlled vehicle intelligence and property security for HOAs, apartments, warehouses and dealerships in Florida.");
    return () => { document.title = prevTitle; };
  }, []);
}


const PILLARS = [
  {
    icon: Eye,
    title: "You own the data",
    body: "Every license-plate read, every alert, every retention window is controlled by your property — never shared with a public network by default.",
  },
  {
    icon: ShieldCheck,
    title: "Technology + Officers, one contract",
    body: "ALPR + CCTV + Access Control tied together with armed / unarmed officers or nightly mobile patrol. One vendor accountable, not four.",
  },
  {
    icon: Scale,
    title: "Regulatory-aware by design",
    body: "Signage kit, retention profiles and HOA / municipal notices adapted per Florida jurisdiction — reviewed regularly.",
  },
  {
    icon: Radio,
    title: "Real-time response",
    body: "Custom hotlists trigger officer dispatch on-property, not a corporate feed. Your team decides who is a stranger.",
  },
];


const SEGMENTS = [
  { icon: Building2, name: "Gated communities & HOAs",
    body: "Multi-entrance perimeter, resident vehicles, guest management." },
  { icon: Building2, name: "Apartment / condo communities",
    body: "Vehicle theft protection, tenant safety, package-thief deterrence." },
  { icon: Car, name: "Auto dealerships",
    body: "Inventory security, after-hours drive-in alerts, service-lane logging." },
  { icon: Building2, name: "Warehouses & logistics",
    body: "Truck-entrance monitoring, driver ID, dock-door integration." },
  { icon: Users, name: "Corporate campuses",
    body: "Employee vs. visitor lots, executive-vehicle protection." },
  { icon: MapPin, name: "Storage / self-storage",
    body: "Perimeter LPR + gate integration + patrol callouts on trigger." },
];


const OUTCOMES = [
  ["94%", "reduction in property vehicle theft after private LPR + patrol combo"],
  ["<60s", "response time from ALPR trigger to on-site officer notification"],
  ["30-90d", "custom retention window — never a fixed corporate policy"],
];


export default function PrivateVehicleSecurityLanding() {
  useSeo();
  return (
    <main className="min-h-screen"
          data-testid="pvs-landing"
          style={{ background: "#0B1120", color: "#E2E8F0",
                    fontFamily: "'Inter',system-ui,sans-serif" }}>

      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-[1180px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[14px] font-extrabold text-white">
                Smith SecureVehicle™
              </div>
              <div className="text-[10px] text-slate-400 -mt-0.5">
                Smith Protective Services · Florida
              </div>
            </div>
          </div>
          <Link to="/security-assessment"
            data-testid="pvs-header-cta"
            className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5"
            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
            Get a Free Assessment <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1180px] mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
        <div>
          <div data-testid="pvs-hero-badge"
               className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 mb-4">
            <Sparkles className="w-3 h-3" /> Private Vehicle Intelligence · not mass surveillance
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight"
              data-testid="pvs-hero-title">
            Know what's entering your property —
            <span className="block" style={{
              background: "linear-gradient(120deg,#F87171,#DC2626)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              on your terms.
            </span>
          </h1>
          <p className="text-[15.5px] text-slate-300 max-w-[560px] mt-5 leading-relaxed"
             data-testid="pvs-hero-lede">
            Smith SecureVehicle™ combines private ALPR cameras, CCTV, gate
            integration and armed / mobile-patrol response into a single
            program — with retention, access and sharing policies that stay
            <em> yours</em>. Deployed on your property, controlled by your team.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/security-assessment"
              data-testid="pvs-hero-cta-primary"
              className="h-11 px-5 rounded-lg text-[13px] font-bold text-white inline-flex items-center gap-1.5"
              style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
              Start Free 2-Minute Assessment <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#pillars"
              data-testid="pvs-hero-cta-secondary"
              className="h-11 px-5 rounded-lg text-[13px] font-bold text-slate-100 border border-white/20 inline-flex items-center gap-1.5 hover:border-red-500/60">
              How it works <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-7 text-[10.5px] text-slate-400 uppercase tracking-widest">
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Customer-owned data
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Florida-licensed officers
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" /> One contract
            </span>
          </div>
        </div>

        {/* Hero visual — score card */}
        <div className="relative">
          <div className="absolute inset-0 blur-3xl opacity-40"
               style={{ background: "radial-gradient(600px 300px at 30% 30%, #DC2626, transparent)" }} />
          <div className="relative rounded-2xl border border-white/10 p-5"
               style={{ background: "linear-gradient(160deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6))" }}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">
              Sample private opportunity report
            </div>
            <div className="rounded-lg p-3 mb-3" style={{ background: "rgba(239,68,68,0.14)" }}>
              <div className="flex items-center gap-2">
                <span className="text-[32px] font-extrabold text-red-400 tabular-nums">92</span>
                <span className="text-[13px] font-bold text-red-300">🔥 Critical Opportunity</span>
              </div>
              <div className="text-[11px] text-slate-300 mt-1">
                Sunset Grove Apts · Orlando FL · 340 spaces · 3 vehicle entrances
              </div>
            </div>
            <ul className="space-y-1.5 text-[11.5px] text-slate-200">
              {[
                "2 recent vehicle-theft reports in adjacent blocks",
                "Existing CCTV but no license-plate recognition",
                "Multi-entrance perimeter → 3 ALPR cameras recommended",
                "Nightly mobile patrol + on-call armed response",
              ].map((r) => (
                <li key={r} className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">Estimated technology + services</span>
              <span className="font-bold text-white tabular-nums">$34k – $52k</span>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-[1180px] mx-auto px-6 pb-10 grid grid-cols-1 md:grid-cols-3 gap-4"
               data-testid="pvs-outcomes">
        {OUTCOMES.map(([stat, label]) => (
          <div key={stat} className="rounded-xl border border-white/10 p-4">
            <div className="text-[32px] font-extrabold text-white tabular-nums">{stat}</div>
            <div className="text-[11.5px] text-slate-400 leading-tight mt-1">{label}</div>
          </div>
        ))}
      </section>

      {/* Pillars */}
      <section id="pillars"
               className="max-w-[1180px] mx-auto px-6 py-14 lg:py-16"
               data-testid="pvs-pillars">
        <div className="max-w-2xl mb-8">
          <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold mb-2">
            Why property managers choose Smith
          </div>
          <h2 className="text-lg md:text-2xl font-extrabold text-white">
            Private vehicle intelligence — designed around your legal reality.
          </h2>
          <p className="text-[13px] text-slate-400 mt-2">
            Public-network LPR programs work for law enforcement — not for
            HOAs, apartments or warehouses that need retention control,
            signage compliance and their <em>own</em> hotlists.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title}
                   data-testid={`pvs-pillar-${p.title.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                   className="rounded-2xl border border-white/10 p-5 hover:border-red-500/40 transition-colors">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                     style={{ background: "rgba(239,68,68,0.14)" }}>
                  <Icon className="w-5 h-5 text-red-300" />
                </div>
                <div className="text-[15px] font-bold text-white">{p.title}</div>
                <div className="text-[12.5px] text-slate-400 mt-1.5 leading-relaxed">
                  {p.body}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Segments */}
      <section className="border-t border-white/5 bg-black/20">
        <div className="max-w-[1180px] mx-auto px-6 py-14"
             data-testid="pvs-segments">
          <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold mb-2">
            Purpose-built for
          </div>
          <h2 className="text-lg md:text-2xl font-extrabold text-white mb-6">
            Properties we protect every day
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {SEGMENTS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.name}
                     className="rounded-xl border border-white/10 p-4 flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                       style={{ background: "rgba(239,68,68,0.14)" }}>
                    <Icon className="w-4 h-4 text-red-300" />
                  </div>
                  <div>
                    <div className="text-[13px] font-bold text-white">{s.name}</div>
                    <div className="text-[11.5px] text-slate-400 mt-0.5">{s.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1180px] mx-auto px-6 py-14"
               data-testid="pvs-how">
        <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold mb-2">
          What happens next
        </div>
        <h2 className="text-lg md:text-2xl font-extrabold text-white mb-6">
          Three steps to your private security plan
        </h2>
        <div className="grid md:grid-cols-3 gap-3">
          {[
            ["1", "Free 2-minute assessment",
              "Answer 16 questions about your property. You get an instant vehicle-security score, recommended package and estimated investment range."],
            ["2", "On-site walkthrough",
              "A Smith advisor visits the property, verifies coverage, and drafts a plan tailored to your Florida jurisdiction."],
            ["3", "Deployment + officers",
              "ALPR cameras + CCTV + gate integration installed. Officers or mobile patrol trained on your custom hotlists."],
          ].map(([n, title, body]) => (
            <div key={n} className="rounded-2xl border border-white/10 p-5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-extrabold text-white mb-3"
                   style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                {n}
              </div>
              <div className="text-[14px] font-bold text-white">{title}</div>
              <div className="text-[12px] text-slate-400 mt-1.5 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-[1180px] mx-auto px-6 pb-14">
        <div className="rounded-2xl p-8 border border-red-500/40"
             style={{
               background: "linear-gradient(120deg, rgba(220,38,38,0.15), rgba(127,29,29,0.1))",
             }}
             data-testid="pvs-cta-banner">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="max-w-xl">
              <h3 className="text-xl md:text-2xl font-extrabold text-white leading-tight">
                See your property's vehicle-security score in 2 minutes.
              </h3>
              <p className="text-[12.5px] text-slate-300 mt-2">
                No sales call required to see your score. A Smith Protective
                advisor follows up only if you want to schedule a site walk.
              </p>
            </div>
            <Link to="/security-assessment"
              data-testid="pvs-banner-cta"
              className="h-11 px-5 rounded-lg text-[13px] font-bold text-white inline-flex items-center gap-1.5 shrink-0"
              style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
              Start Free Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Regulatory + footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-[1180px] mx-auto px-6 py-8 space-y-4">
          <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-[11px] text-amber-200 flex items-start gap-2"
               data-testid="pvs-regulatory-note">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <strong>Regulatory notice.</strong> ALPR deployment, data
              retention, access policies, signage, HOA authority, local
              regulations and law-enforcement sharing requirements must be
              reviewed for each property and jurisdiction. Smith Protective
              provides Private Vehicle Intelligence with customer-controlled
              security policies — not a public surveillance network.
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
            <div>© {new Date().getFullYear()} Smith Protective Services. All rights reserved.</div>
            <div className="flex items-center gap-3">
              <Link to="/security-assessment" className="hover:text-white">Assessment</Link>
              <Link to="/" className="hover:text-white">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
